import AtlassianSwitcher from "@atlassian/switcher";
import {
  createAvailableProductsProvider,
  createJoinableSitesProvider,
  createProductConfigurationProvider,
} from "@atlassian/switcher/create-custom-provider";

import styled from "styled-components";
import React, { useState, useEffect } from "react";
import { IntlProvider } from "react-intl";
import Environment from "../../common/environment";
import AppSwitcherIcon from "@atlaskit/icon/glyph/app-switcher";

import { hamletUser } from "../../common/hamlet/hamlet-user";
import WacLDClient from "../../common/LaunchDarklyClient";
import { JoinableSite } from "@atlassian/switcher/dist/types/types";


type JoinableSitesResponseJson = {
  joinableSites?: Array<JoinableSite>;
};

const AVATAR_IMG_URL =
  "http://wac-cdn.atlassian.com/dam/wac/icons/ADG3-Icons-36px-N700/object-avatar-man.svg";

const WacAppSwitcher = (props: { allowWacJoinSitesProvider: boolean }) => {
  const [isAppSwitcherMenu, setAppSwitcherMenu] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [joinableSites, setJoinableSites] = useState(null);
  const [isFirstSwitcherOpen, setIsFirstSwitcherOpen] = useState(true);
  const [
    enableRemoteProductConfiguration,
    setEnableRemoteProductConfiguration,
  ] = useState(false);
  const [enableRemoteAdminConfiguration, setEnableRemoteAdminConfiguration] =
    useState(false);
  // only load <AtlassianSwitcher/> on 1st hover
  const [hasHovered, setHasHovered] = useState(false);
  // this would allow the app switcher to work on author and local
  const customAvailableProductsDataProvider = createAvailableProductsProvider(
    `${Environment.getAPIPrivateURL()}/available-products/api/available-products`
  );
  const productConfigurationProvider = createProductConfigurationProvider({
    url: `${Environment.getAPIPrivateURL()}/available-products/api/product-configuration`,
    useRemoteProductConfiguration: true,
  });

  async function getUserData() {
    const userInfo = await getUserInfo();
    if (userInfo.user && userInfo.user.myDetails) {
      loadFeatureFlags(userInfo.user.myDetails.aaid);
    }
    setIsLoggedIn(userInfo.isLoggedIn() || false);
  }

  async function getJoinableSites() {
    const sites: JoinableSitesResponseJson = await fetch(
      `${Environment.getAPIPrivateURL()}/joinable-sites`,
      {
        method: "POST",
        credentials: "include",
        body: JSON.stringify({
          products: [
            "confluence.ondemand",
            "jira-software.ondemand",
            "jira-servicedesk.ondemand",
            "jira-core.ondemand",
          ],
        }),
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
      }
    ).then((res) => res.json());

    const reMappedSitesData = (sites.joinableSites || []).map((site) => {
      const userData: any = {};
      Array.prototype.forEach.call(site.products, (val: string | number) => {
        userData[val] = [{ avatarUrl: AVATAR_IMG_URL, displayName: "" }];
      });
      return {
        cloudId: site.cloudId ? site.cloudId : "",
        displayName: site.displayName ? site.displayName : "",
        url: site.url ? site.url : "",
        avatarUrl: site.avatarUrl ? site.avatarUrl : undefined,
        relevance: site.relevance ? site.relevance : undefined,
        users: userData,
      };
    });

    const joinableSitesPromiseMaker = () => {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({ sites: reMappedSitesData });
        });
      });
    };

    setJoinableSites(createJoinableSitesProvider(joinableSitesPromiseMaker));
  }

  const loadFeatureFlags = (userKey: string) => {
    const ldClient = new WacLDClient({
      key: userKey,
    });
    ldClient.waitForInitialization().then(() => {
      setEnableRemoteProductConfiguration(
        ldClient.variation(
          "app-switcher-enable-remote-product-configuration",
          false
        )
      );
      setEnableRemoteAdminConfiguration(
        ldClient.variation(
          "app-switcher-enable-remote-admin-configuration",
          false
        )
      );
    });
  };

  document.addEventListener("click", (event: any) => {
    const path = event.path || (event.composedPath && event.composedPath());
    const insideAppswitcher = Array.prototype.includes.call(
      Array.prototype.map.call(path, (el: { id: string }) => {
        return el.id === "global-nav--wac__app-switcher";
      }),
      true
    );

    if (!insideAppswitcher) {
      setAppSwitcherMenu(false);
    }
  });

  useEffect(() => {
    if (props.allowWacJoinSitesProvider) {
      getJoinableSites();
    }
    getUserData();
  }, []);

  const theme = {
    primaryTextColor: "#172b4d",
    secondaryTextColor: "#5e6c84",
    primaryHoverBackgroundColor: "#E0E2E5",
    secondaryHoverBackgroundColor: "#F5F6F7",
  };

  const switcher = (
    <AppSwitcherMenu>
      <AtlassianSwitcher
        product="WAC"
        appearance="standalone"
        theme={theme}
        joinableSitesDataProvider={joinableSites}
        availableProductsDataProvider={customAvailableProductsDataProvider}
        productConfigurationDataProvider={productConfigurationProvider}
        onJoinableSiteClicked={() => {
          setAppSwitcherMenu(false);

          // joinable sites might now change if user follows through joining this site
          // so dump joinable sites data to indicate it must be reloaded
          setJoinableSites(null);
        }}
        enableRemoteConfiguration={enableRemoteProductConfiguration}
        enableRemoteAdminLinks={enableRemoteAdminConfiguration}
      />
    </AppSwitcherMenu>
  );

  if (!isLoggedIn) {
    return null;
  }

  return (
    <IntlProvider locale={document.documentElement.lang}>
      <AppSwitcherContainer>
        <StyledAppSwitcherIcon
          onClick={() => {
            if (isFirstSwitcherOpen) {
              setIsFirstSwitcherOpen(false);
            } else if (joinableSites === null) {
              // reload the joinable sites data if a joinable site has been clicked
              if (props.allowWacJoinSitesProvider) {
                getJoinableSites();
              }
            }
            setAppSwitcherMenu(!isAppSwitcherMenu);
          }}
          onMouseEnter={() => setHasHovered(true)}
        >
          <AppSwitcherIcon
            size={"large"}
            primaryColor="#0747a6"
            label="App Switcher"
          />
        </StyledAppSwitcherIcon>
        <Container displayVal={isAppSwitcherMenu}>
          {hasHovered && switcher}
        </Container>
      </AppSwitcherContainer>
    </IntlProvider>
  );
};

const getUserInfo = () => {
  const PERFORMANCE_BOOST_COOKIE = "WCF-512";
  const performanceBoost =
    imkt.utils.storage.getCookie(PERFORMANCE_BOOST_COOKIE) !== null;
  return hamletUser.getStargateProfile(performanceBoost).then(() => {
    return hamletUser;
  });
};

const Container = styled.div`
  display: ${(props: { displayVal: boolean }) =>
    props.displayVal ? "block" : "none"};
  position: relative;
  right: 330px;
  top: 36px;
`;

const AppSwitcherContainer = styled.div`
  display: flex;
  justify-content: center;

  :hover {
    background: #ebecf0;
    border-radius: 25px;
  }
  ${Container}:hover {
    display: block;
  }
`;

const StyledAppSwitcherIcon = styled.div`
  span {
    height: 28px;
    width: 28px;
  }
  display: flex;
  justify-content: center;
  cursor: pointer;
`;

const AppSwitcherMenu = styled.div`
  display: block;
  position: absolute;
  border-radius: 5px;
  background-color: white;
  min-width: 160px;
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
  padding: 8px 16px 12px 16px;
  z-index: 1;
  svg {
    margin-bottom: 0;
  }
  h1 {
    margin-top: 0px;
    font-weight: normal;
    font-weight: 500;
  }
  a {
    font-weight: 400;
  }
`;

export default WacAppSwitcher;
